import { formatMoney } from "../../utils/format"

// Wording for objections: a customer disputes a bill amount, and the bill is not
// paid until the company enters the amount again.

export const MAX_OBJECTION_MESSAGE_LENGTH = 500

const formatKes = (value) => `KES ${formatMoney(value)}`

export const isObjectionOpen = (bill) => bill?.objection?.status === "open"

// Why the typed message cannot be sent yet, or "" when it can.
export const getObjectionMessageError = (message) => {
  const text = String(message ?? "").trim()
  if (!text) return "Please say why the amount is wrong."
  if (text.length > MAX_OBJECTION_MESSAGE_LENGTH) {
    return `Please keep your message under ${MAX_OBJECTION_MESSAGE_LENGTH} characters.`
  }
  return ""
}

export const buildObjectBillConfirm = (bill = {}, company = {}) => {
  const name = company.providerName || "The company"
  return {
    title: "Object to this amount?",
    message: `${name} will be asked to check the amount and enter it again. This bill is not paid until they do.`,
    details: [
      { label: "Payment requester", value: company.providerName || "Billing company" },
      { label: "Bill", value: bill.description || "Bill" },
      { label: "Amount", value: formatKes(bill.amount ?? bill.outstandingAmount) },
    ],
    confirmLabel: "Send objection",
  }
}

export const describeObjectionSent = (company = {}) =>
  `Objection sent. ${company.providerName || "The company"} must enter the amount again before this bill is paid.`

// The line under a bill that has an objection, as the customer sees it.
export const describeCustomerObjection = (objection, companyName = "") => {
  if (!objection?.status) return null
  const name = companyName || "the company"
  if (objection.status === "open") {
    return { tone: "rose", title: `Objected · waiting for ${name} to check the amount`, message: objection.message }
  }
  const before = Number(objection.objectedAmount || 0)
  const after = Number(objection.answeredAmount ?? before)
  return {
    tone: "slate",
    title: after === before
      ? `${name} checked your objection and kept ${formatKes(after)}`
      : `${name} checked your objection: ${formatKes(before)} is now ${formatKes(after)}`,
    message: objection.message,
  }
}

// The same objection, as the billing company sees it on its bill list.
export const describeBillerObjection = (objection) => {
  if (!objection?.status) return null
  if (objection.status === "open") {
    return { tone: "rose", title: "Customer objected to the amount. Enter it again.", message: objection.message }
  }
  return { tone: "slate", title: `You answered with ${formatKes(objection.answeredAmount)}`, message: objection.message }
}

// Dashboard bell rows.
export const describeBillerNotice = (notice = {}) => ({
  title: `${notice.customerName || "A customer"} objected to a bill`,
  subtitle: `${notice.providerName || "Your company"} · ${notice.description || "Bill"} · ${formatKes(notice.amount)}: "${
    notice.objection?.message || ""
  }"`,
})

export const describeCustomerNotice = (notice = {}) => {
  const answered = Number(notice.objection?.answeredAmount ?? notice.amount ?? 0)
  return {
    title: `${notice.providerName || "A company"} checked your objection`,
    subtitle: `${notice.description || "Your bill"} is now ${formatKes(answered)}. Pay it, or object again.`,
  }
}
